import React from 'react';
import { UserProfile } from '@/shared/types';
import { VARIANT_CONFIG } from '@/features/templates/components/CorporateSuite/configs/variantConfigs';

interface HeaderProps {
  profile: UserProfile;
  title: string;
  type: string;
  metaData: { label: string; value: string }[];
  config: any;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
}

export const SplitHeroHeader: React.FC<HeaderProps> = ({ profile, title, type, metaData, config, LogoImage }) => (
  <div className={`flex justify-between p-12 ${config.header === 'split-hero-navy' ? 'bg-[#0f172a] text-white' : 'bg-gray-50 border-b border-gray-200'}`}>
    <div className="flex flex-col justify-between">
      <LogoImage size="md" light={config.header === 'split-hero-navy'} />
      <div className="mt-6">
        <h1 className="text-3xl font-bold uppercase tracking-tight">{profile.name}</h1>
        <p className={`text-sm ${config.header === 'split-hero-navy' ? 'text-gray-400' : 'text-gray-500'}`}>{profile.email}</p>
      </div>
    </div>
    <div className="text-right flex flex-col justify-between">
      <h2 className={`text-5xl font-black uppercase tracking-tighter ${config.header === 'split-hero-navy' ? 'text-gray-700' : 'text-gray-200'}`}>{type}</h2>
      <div>
        <p className={`font-bold ${config.header === 'split-hero-navy' ? 'text-primary-400' : 'text-primary-600'}`}>{title}</p>
        {metaData.map(m => <p key={m.label} className="text-xs uppercase tracking-wider opacity-70">{m.label}: {m.value}</p>)}
      </div>
    </div>
  </div>
);

export const CenteredClassicHeader: React.FC<HeaderProps> = ({ profile, LogoImage }) => (
  <div className="p-12 text-center border-b-2 border-gray-900 mb-8">
    <div className="flex justify-center mb-6"><LogoImage size="lg"/></div>
    <h1 className="text-4xl font-bold uppercase tracking-widest text-gray-900 mb-2">{profile.name}</h1>
    <div className="flex justify-center gap-6 text-sm text-gray-500 font-medium uppercase tracking-wider">
      <span>{profile.address}</span>
      <span>{profile.email}</span>
    </div>
  </div>
);

export const DarkHeader: React.FC<HeaderProps> = ({ profile, type, metaData, LogoImage }) => (
  <div className="bg-black text-white p-12 flex justify-between items-end">
    <div>
      <LogoImage size="md" light className="mb-4" />
      <h1 className="text-2xl font-bold">{profile.name}</h1>
      <p className="text-sm text-gray-400">{profile.address}</p>
    </div>
    <div className="text-right">
      <div className="bg-white text-black font-bold px-4 py-1 text-sm inline-block mb-2">{type.toUpperCase()}</div>
      {metaData.map(m => (
        <div key={m.label} className="flex justify-end gap-4 text-sm">
          <span className="text-gray-500">{m.label}</span>
          <span className="font-mono">{m.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const MinimalGridHeader: React.FC<HeaderProps> = ({ profile, type, metaData, LogoImage }) => (
  <div className="p-12 grid grid-cols-2 border-b-4 border-black">
    <div className="border-r border-black pr-8">
      <h1 className="text-3xl font-bold">{profile.name}</h1>
      <p className="text-sm mt-2">{profile.address}</p>
    </div>
    <div className="pl-8 flex flex-col justify-between">
      <div className="flex justify-between items-start">
        <h2 className="text-xl font-bold uppercase">{type}</h2>
        <LogoImage size="sm"/>
      </div>
      <div className="text-right text-xs font-mono">
        {metaData.map(m => <p key={m.label}>{m.label}: {m.value}</p>)}
      </div>
    </div>
  </div>
);

export const HeroBlockHeader: React.FC<HeaderProps> = ({ profile, type, metaData, LogoImage }) => (
  <div className="relative bg-gray-900 text-white p-16 overflow-hidden">
    <div className="absolute top-0 right-0 w-64 h-full bg-primary-600 transform -skew-x-12 translate-x-16"></div>
    <div className="relative z-10 flex justify-between items-center">
      <div className="flex items-center gap-6">
        <LogoImage size="md" light className="bg-white/10 p-2 rounded" />
        <div>
          <h1 className="text-3xl font-bold">{profile.name}</h1>
          <p className="text-primary-200">{type}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-4xl font-bold">{metaData.find(m => m.label.includes('Ref'))?.value}</p>
        <p className="text-sm opacity-60">Reference ID</p>
      </div>
    </div>
  </div>
);

export const GradientTopHeader: React.FC<HeaderProps> = () => (
  <div className="h-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 w-full mb-8"></div>
);

export const ArtisticHeroHeader: React.FC<HeaderProps> = ({ profile, LogoImage }) => (
  <div className="p-12 pb-24 relative bg-gray-50">
    <div className="absolute bottom-0 left-0 w-full h-12 bg-white rounded-t-[50%]"></div>
    <div className="flex justify-between items-center relative z-10">
      <h1 className="text-5xl font-black text-gray-900 tracking-tighter">{profile.name}</h1>
      <LogoImage size="md" />
    </div>
  </div>
);

export const DefaultHeader: React.FC<HeaderProps> = ({ profile, title, type, LogoImage }) => (
  <div className="p-12 flex justify-between items-start border-b border-gray-100">
    <div className="flex gap-4">
      <LogoImage size="md" />
      <div>
        <h1 className="text-xl font-bold text-gray-900">{profile.name}</h1>
        <p className="text-sm text-gray-500">{profile.email}</p>
      </div>
    </div>
    <div className="text-right">
      <h2 className="text-2xl font-bold text-primary-600">{type}</h2>
      <p className="text-sm text-gray-400">{title}</p>
    </div>
  </div>
);